import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { calloutCss, setLiveCalloutConfig } from "@/lib/kbCalloutStyles";

// Session cache so navigating between KB pages doesn't refetch the stylesheet.
let _cache = null;
let _promise = null;

export async function fetchKbSettings(force = false) {
  if (_cache && !force) return _cache;
  if (!_promise || force) {
    _promise = api.get("/kb/settings").then((r) => { _cache = r.data; return r.data; })
      .catch(() => ({}));
  }
  return _promise;
}
export function setKbSettingsCache(s) { _cache = s; }

const FONT_STACK = {
  Manrope: "'Manrope', system-ui, sans-serif",
  "IBM Plex Sans": "'IBM Plex Sans', system-ui, sans-serif",
  Inter: "'Inter', system-ui, sans-serif",
  Georgia: "Georgia, 'Times New Roman', serif",
  "Courier New": "'Courier New', monospace",
};
const stack = (f) => FONT_STACK[f] || (f ? `'${f}', system-ui, sans-serif` : "inherit");
const GOOGLE_FONTS = new Set(["Manrope", "IBM Plex Sans", "Inter", "Roboto", "Lora", "Merriweather", "Nunito Sans", "Source Sans 3", "Work Sans"]);

export function buildKbCss(s) {
  if (!s) return "";
  const brand = "hsl(var(--primary))";
  const link = s.use_brand_colors || !s.link_color ? brand : s.link_color;
  const heading = s.heading_color || "#0f172a";
  const width = s.content_width && s.content_width > 0 ? `${s.content_width}px` : "none";
  const stripe = s.table_stripe ? "hsl(210 40% 98.5%)" : "transparent";
  return `
.kb-prose{ font-family:${stack(s.body_font)}; font-size:${s.base_font_size || 15}px; line-height:${s.line_height || 1.75}; color:${s.text_color || "#334155"}; }
#kb-article-body, .kb-editor-body{ max-width:${width}; margin-left:auto; margin-right:auto; }
.kb-prose h1,.kb-prose h2,.kb-prose h3,.kb-prose h4{ font-family:${stack(s.heading_font)}; color:${heading}; }
.kb-prose a{ color:${link}; }
.kb-prose thead, .kb-prose th, .kb-prose table:not(:has(th)) > tbody > tr:first-child > td{ background:${s.table_header_bg || "#f1f5f9"}; }
.kb-prose table, .kb-prose th, .kb-prose td{ border-color:${s.table_border_color || "#e2e8f0"}; }
.kb-prose tbody tr:nth-child(even){ background:${stripe}; }
${s.custom_css || ""}
`;
}

function loadFonts(s) {
  const fams = [s.body_font, s.heading_font].filter((f) => f && GOOGLE_FONTS.has(f));
  if (!fams.length) return;
  const q = fams.map((f) => `family=${f.replace(/ /g, "+")}:wght@400;500;600;700;800`).join("&");
  const id = "kb-google-fonts";
  let link = document.getElementById(id);
  const href = `https://fonts.googleapis.com/css2?${q}&display=swap`;
  if (!link) {
    link = document.createElement("link");
    link.id = id; link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

/** Injects the KB stylesheet into <head>. Mount inside any KB page. */
export function KbStyleInjector() {
  useEffect(() => {
    let alive = true;
    fetchKbSettings().then((s) => {
      if (!alive || !s) return;
      loadFonts(s);
      const id = "kb-dynamic-style";
      let el = document.getElementById(id);
      if (!el) { el = document.createElement("style"); el.id = id; document.head.appendChild(el); }
      el.textContent = buildKbCss(s);
    });
    // Callout appearance (shape preset + per-type swatch/icon) lives in its own
    // setting so editors can restyle boxes without touching typography.
    api.get("/kb/callout-styles").then(({ data }) => {
      if (!alive) return;
      const id = "kb-callout-styles";
      let el = document.getElementById(id);
      if (!el) { el = document.createElement("style"); el.id = id; document.head.appendChild(el); }
      el.textContent = calloutCss(data);
      setLiveCalloutConfig(data);
    }).catch(() => { /* stylesheet defaults stay */ });
    return () => { alive = false; };
  }, []);
  return null;
}

const KbCtx = createContext({ settings: null, refresh: () => {} });
export function useKbSettings() { return useContext(KbCtx); }
